import Cookies from "js-cookie";
import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import {
  hasPartnerPortalAccess,
  useAuth,
} from "@/context";
import { usePermissions } from "@/context/permissions";
import {
  CampaignDetailsPage,
  CampaignsPage,
  CampaignUploadPage,
  ClientRegisterPage,
  ClientsPage,
  ConfirmAccount,
  DashboardPage,
  EditProjectPage,
  InsuranceCompaniesPage,
  InvoicesPage,
  Login,
  NoPermissionsPage,
  NotFoundPage,
  PartnerSelectPage,
  PartnerWhatsappInboxPage,
  PaymentGatewaysPage,
  PlatformPage,
  ProductsEditPage,
  ProductsPage,
  ProjectDetailPage,
  ProjectsListPage,
  ProjectsPage,
  RegisterPage,
  ReportsPage,
  ResetPassword,
  SettingsPage,
  WhatsappMetaConfigDetailPage,
  WhatsappMetaConfigPage,
} from "@/pages";
import { ClientDetailPage,PlanFormPage, PlansPage, SubPartnerDetailPage, SubPartnerRegisterPage, SubPartnersPage } from "@/pages/clients";
import {
} from "@/pages/clients/components/client-form";
import { CreatePlanPage } from "@/pages/projects";
import { UsersPage } from "@/pages/users";
import TeamFormPage from "@/pages/users/team-form/team-form.page";

import { PermissionWrapper } from "./permission-wrapper";
import { ProtectedRoute } from "./protected-route";
import { PublicRoute } from "./public-route";
import { useFirstAllowedRoute } from "./route-helpers";

export function AppRoutes() {
  const { auth } = useAuth();
  const location = useLocation();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const firstAllowedRoute = useFirstAllowedRoute();

  // Verificar se há token no cookie (para casos de reload da página)
  const hasTokenInCookie = React.useMemo(() => {
    try {
      const authUserCookie = Cookies.get("authUser");
      if (authUserCookie) {
        const authUser = JSON.parse(authUserCookie);
        return !!authUser?.token;
      }
    } catch (error) {
      // Ignorar erro de parse
    }
    return false;
  }, []);

  // Calcular a rota de destino baseada nas permissões
  const getDestinationRoute = React.useMemo(() => {
    // Se ainda está carregando permissões, não fazer nada ainda
    if (permissionsLoading) {
      return null;
    }
    
    // Se não está autenticado E não tem token no cookie, não calcular rota
    // (permite que usuários com token no cookie mas auth ainda não restaurado continuem)
    if (!auth.isAuthenticated && !hasTokenInCookie) {
      return null;
    }

    let roles = auth.roles;
    let organizationRootId = auth.organization_root_id;
    if (hasTokenInCookie) {
      try {
        const fromCookie = JSON.parse(Cookies.get("authUser") || "{}");
        roles = roles ?? fromCookie.roles;
        organizationRootId =
          organizationRootId || fromCookie.organization_root_id;
      } catch {
        // ignore
      }
    }

    if (
      hasPartnerPortalAccess(roles) &&
      organizationRootId
    ) {
      return `/clients/${organizationRootId}`;
    }
    
    // Priorizar dashboard se o usuário tiver permissão
    const hasDashboardPermission = hasPermission("dashboard:view");
    
    if (hasDashboardPermission) {
      return "/dashboard";
    }
    
    // Caso contrário, usar a primeira rota permitida
    const destination = firstAllowedRoute;
    
    // IMPORTANTE: Só redirecionar para /no-permissions se tiver CERTEZA ABSOLUTA que não tem nenhuma permissão
    // Isso só acontece quando:
    // 1. As permissões já foram carregadas (permissionsLoading === false)
    // 2. O usuário está autenticado OU tem token no cookie
    // 3. Não encontrou nenhuma rota permitida (firstAllowedRoute === null)
    // NÃO redirecionar se ainda está carregando ou se não está autenticado
    if (!destination && (auth.isAuthenticated || hasTokenInCookie)) {
      return "/no-permissions";
    }
    
    return destination;
  }, [
    hasPermission,
    permissionsLoading,
    firstAllowedRoute,
    auth.isAuthenticated,
    hasTokenInCookie,
    auth.roles,
    auth.organization_root_id,
  ]);

  // Restauração de auth movida para AuthProvider (useState initializer síncrono).
  // Este useEffect foi removido para eliminar a race condition com ProtectedRoute.

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            auth.isAuthenticated && !location.state?.from ? (
              // Aguardar carregamento das permissões antes de redirecionar
              permissionsLoading || getDestinationRoute === null ? (
                null // Aguardar carregamento das permissões
              ) : (() => {
                  let roles = auth.roles;
                  let organizationRootId = auth.organization_root_id;
                  try {
                    const fromCookie = JSON.parse(
                      Cookies.get("authUser") || "{}"
                    );
                    roles = roles ?? fromCookie.roles;
                    organizationRootId =
                      organizationRootId || fromCookie.organization_root_id;
                  } catch {
                    // ignore
                  }
                  if (
                    hasPartnerPortalAccess(roles) &&
                    organizationRootId
                  ) {
                    return (
                      <Navigate
                        to={`/clients/${organizationRootId}`}
                        replace
                      />
                    );
                  }
                  return (
                    <Navigate to={getDestinationRoute} replace />
                  );
                })()
            ) : (
              <Login />
            )
          }
        />

        <Route element={<PublicRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/confirm-account" element={<ConfirmAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/cadastro-novo-cliente/:affiliateId" element={<ClientRegisterPage />} />
          <Route path="/cadastro-organizacao" element={<ClientRegisterPage />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route
            path="/dashboard"
            element={
              <PermissionWrapper
                permission="dashboard:view"
                redirectTo="/clients"
              >
                <DashboardPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <ClientsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <ClientDetailPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/select/:intent"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <PartnerSelectPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/plans"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <PlansPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/plan-form"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <PlanFormPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/sub-partners"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <SubPartnersPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/sub-partners/new"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <SubPartnerRegisterPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/sub-partners/:subId"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <SubPartnerDetailPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/sub-partners/:subId/edit"
            element={
              <PermissionWrapper
                permissions={["partners:view", "partner:self:view"]}
                requireAll={false}
              >
                <SubPartnerRegisterPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/projects"
            element={
              <PermissionWrapper permission="project:create">
                <ProjectsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/projects/list"
            element={
              <PermissionWrapper permission="project:list">
                <ProjectsListPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/projects/:projectId"
            element={
              <PermissionWrapper permission="project:view">
                <ProjectDetailPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/projects/:projectId/edit"
            element={
              <PermissionWrapper permission="project:edit">
                <EditProjectPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/projects/:projectId/create-plan"
            element={
              <PermissionWrapper permission="plan:create">
                <CreatePlanPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/projects/:projectId/platforms"
            element={
              <PermissionWrapper permission="platform:view">
                <PlatformPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/campaigns"
            element={
              <PermissionWrapper permission="campaign:view">
                <CampaignsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/whatsapp"
            element={
              <PermissionWrapper permission="whatsapp:view">
                <PartnerWhatsappInboxPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/whatsapp-meta"
            element={
              <PermissionWrapper permission="whatsapp:view">
                <WhatsappMetaConfigPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/whatsapp-meta/:configId"
            element={
              <PermissionWrapper permission="whatsapp:view">
                <WhatsappMetaConfigDetailPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/campaigns/:campaignId/upload"
            element={
              <PermissionWrapper permission="campaign:upload">
                <CampaignUploadPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/campaigns/:campaignId/details"
            element={
              <PermissionWrapper permission="campaign:view">
                <CampaignDetailsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/whatsapp-meta"
            element={
              <PermissionWrapper permission="whatsapp:view">
                <WhatsappMetaConfigPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/whatsapp-meta/:configId"
            element={
              <PermissionWrapper permission="whatsapp:view">
                <WhatsappMetaConfigDetailPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/users"
            element={
              <PermissionWrapper permission="user:view">
                <UsersPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/team-form"
            element={
              <PermissionWrapper permission="user:view">
                <TeamFormPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/invoices"
            element={
              <PermissionWrapper permission="invoices:view">
                <InvoicesPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/clients/:clientId/platforms"
            element={
              <PermissionWrapper permission="platform:view">
                <PlatformPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/products"
            element={
              <PermissionWrapper permission="products:view">
                <ProductsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/products/:id"
            element={
              <PermissionWrapper permission="products:view">
                <ProductsEditPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/reports"
            element={
              <PermissionWrapper permission="reports:view">
                <ReportsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/settings"
            element={
              <PermissionWrapper permission="settings:view">
                <SettingsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/insurance-companies"
            element={
              <PermissionWrapper permission="insurance:view">
                <InsuranceCompaniesPage />
              </PermissionWrapper>
            }
          />
          <Route
            path="/payment-gateways"
            element={
              <PermissionWrapper permission="payment_gateways:view">
                <PaymentGatewaysPage />
              </PermissionWrapper>
            }
          />
          <Route path="/no-permissions" element={<NoPermissionsPage />} />
        </Route>

        {/* Rota 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
